import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
} from '@mui/material';
import theme from '../../../../theme';

/**
 * Modal for capturing a rejection reason when a deficiency is rejected
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when rejection is confirmed
 * @param {string} props.title - Optional title override
 * @returns {React.ReactElement} Rejection reason modal dialog
 */
const RejectionReasonModal = ({ open, onClose, onConfirm, title = 'Reject Deficiency' }) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!reason.trim()) {
            setError('A rejection reason is required');
            return;
        }
        onConfirm(reason);
        setReason('');
        setError('');
    };

    const handleClose = () => {
        setReason('');
        setError('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: '450px',
                    borderRadius: '12px',
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Please provide a reason for rejecting this work. This will be visible to the
                        assignee and recorded in the history.
                    </Typography>

                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        placeholder="e.g., Still seeing cracks in the drywall near the corner..."
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (e.target.value.trim()) setError('');
                        }}
                        error={!!error}
                        helperText={error}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            },
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: theme.palette.error.dark,
                            boxShadow: 'none',
                        },
                    }}
                >
                    Confirm Rejection
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RejectionReasonModal;
